
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useAppStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { AppUser } from "@/lib/types";

const links = [
  { href: "/marketplace", label: "Marketplace" },
  { href: "/#categories", label: "Categories" },
  { href: "/sell", label: "Sell" },
  { href: "/marketplace?rail=rare-finds", label: "Collections" },
];

// Mirrors the post-login redirect in LoginPage — there's no generic buyer
// account page, so a signed-in buyer's "Account" icon goes to the
// marketplace rather than a role-restricted dashboard that would 404 them
// into "Restricted area."
function accountHref(user: AppUser | null) {
  if (!user) return "/login";
  if (user.role === "admin") return "/dashboard/admin";
  if (user.role === "seller") return "/dashboard/seller";
  return "/marketplace";
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const cart = useAppStore((s) => s.cart);
  const products = useAppStore((s) => s.products);
  const user = useAppStore((s) => s.currentUser());
  const favouritesCount = useAppStore((s) =>
    user ? (s.favourites[user.uid]?.length ?? 0) : 0
  );
  const accountDestination = accountHref(user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Excludes items whose product was since deleted (e.g. by an admin) —
  // otherwise the badge stays stale even though /cart itself already
  // filters these out and shows "Your cart is empty."
  const cartCount = cart
    .filter((c) => products.some((p) => p.id === c.productId))
    .reduce((n, c) => n + c.quantity, 0);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-[#061331e8] backdrop-blur-xl border-b border-cyan-200/15 shadow-[0_12px_40px_rgba(0,10,40,.25)]"
          : "bg-gradient-to-b from-[#04102bd9] to-transparent border-b border-transparent"
      )}
    >
      <div className="nm-container flex h-16 md:h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="relative rounded-xl bg-[#071a42] shadow-[0_0_22px_rgba(34,225,255,.2)]">
            <img
            src="/images/logo-mark-v3.webp"
            alt=""
            className="h-10 w-10 md:h-11 md:w-11 object-contain"
            />
          </span>
          <span className="font-display text-base md:text-lg font-black tracking-[-0.03em] text-white">
            NERD <span className="text-[var(--nm-gold)]">MULTIVERSE</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className="text-xs font-semibold uppercase tracking-[0.12em] text-white/70 hover:text-[var(--nm-gold)] transition-colors duration-200"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <Link
            to="/marketplace"
            className="hidden md:flex items-center justify-center w-10 h-10 rounded-xl text-white/70 hover:text-[var(--nm-gold)] hover:bg-white/8 transition-colors"
            aria-label="Search"
          >
            <Search size={18} />
          </Link>
          <Link
            to="/favourites"
            className="relative flex items-center justify-center w-10 h-10 rounded-full text-[var(--nm-ink-muted)] hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Favourites"
          >
            <Heart size={18} />
            {favouritesCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[var(--nm-pink)]" />
            )}
          </Link>
          <Link
            to="/cart"
            className="relative flex items-center justify-center w-10 h-10 rounded-full text-[var(--nm-ink-muted)] hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={18} />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center text-[10px] font-medium w-4 h-4 rounded-full bg-[var(--nm-blue)] text-white">
                {cartCount}
              </span>
            )}
          </Link>
          <Link
            to={accountDestination}
            className="hidden md:flex items-center justify-center w-10 h-10 rounded-full text-[var(--nm-ink-muted)] hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Account"
          >
            <User size={18} />
          </Link>
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full text-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#0c1222] border-b border-white/10"
          >
            <nav className="nm-container flex flex-col py-4 gap-1">
              {links.map((l) => (
                <Link
                  key={l.label}
                  to={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-base text-[var(--nm-ink-muted)] hover:text-white transition-colors border-b border-white/5 last:border-0"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to={accountDestination}
                onClick={() => setMobileOpen(false)}
                className="py-3 text-base text-[var(--nm-ink-muted)] hover:text-white transition-colors"
              >
                {user ? (user.role === "buyer" ? "Account" : "Dashboard") : "Sign in"}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
